import React, { useState, useEffect, useCallback } from 'react';
import './DianaPanel.css';

const API = '';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs} h`;
  return `hace ${Math.floor(hrs / 24)} días`;
}

export default function DianaPanel({ onClose, socket }) {
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [saveToKnowledge, setSaveToKnowledge] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  // Manual knowledge entry
  const [manualQ, setManualQ] = useState('');
  const [manualA, setManualA] = useState('');
  const [addingKnowledge, setAddingKnowledge] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadPending = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/diana/pending`, { headers });
      const data = await r.json();
      setPending(Array.isArray(data) ? data : []);
    } catch (e) { console.error('Diana pending error', e); }
  }, []);

  const loadKnowledge = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/diana/knowledge`, { headers });
      const data = await r.json();
      setKnowledge(Array.isArray(data) ? data : []);
    } catch (e) { console.error('Diana knowledge error', e); }
  }, []);

  useEffect(() => {
    loadPending();
    loadKnowledge();
  }, [loadPending, loadKnowledge]);

  useEffect(() => {
    if (!socket) return;
    const handler = () => loadPending();
    socket.on('diana-pending-count', handler);
    return () => socket.off('diana-pending-count', handler);
  }, [socket, loadPending]);

  const handleAnswer = async () => {
    if (!selectedId || !answerText.trim()) {
      setStatus('⚠️ Selecciona una pregunta y escribe la respuesta');
      return;
    }
    setSending(true);
    setStatus('Enviando...');
    try {
      const r = await fetch(`${API}/api/diana/answer/${selectedId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ answer: answerText.trim(), saveToKnowledge })
      });
      const data = await r.json();
      if (data.success) {
        setStatus('✅ Diana respondió al cliente' + (saveToKnowledge ? ' y guardó en su conocimiento' : ''));
        setAnswerText('');
        setSelectedId(null);
        await loadPending();
        await loadKnowledge();
      } else {
        setStatus('❌ Error: ' + (data.error || 'desconocido'));
      }
    } catch (e) {
      setStatus('❌ Error de conexión');
    }
    setSending(false);
  };

  const handleDeleteKnowledge = async (id) => {
    if (!window.confirm('¿Eliminar esta entrada del conocimiento?')) return;
    try {
      await fetch(`${API}/api/diana/knowledge/${id}`, { method: 'DELETE', headers });
      setKnowledge(k => k.filter(e => e._id !== id));
    } catch (e) { alert('Error al eliminar'); }
  };

  const handleAddKnowledge = async () => {
    if (!manualQ.trim() || !manualA.trim()) return;
    setAddingKnowledge(true);
    try {
      const r = await fetch(`${API}/api/diana/knowledge`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ question: manualQ.trim(), answer: manualA.trim() })
      });
      const data = await r.json();
      if (data._id) {
        setKnowledge(k => [data, ...k]);
        setManualQ('');
        setManualA('');
      }
    } catch (e) { alert('Error al agregar'); }
    setAddingKnowledge(false);
  };

  const selectedPending = pending.find(p => p._id === selectedId);

  return (
    <div className="diana-panel-overlay" onClick={(e) => { if (e.target.classList.contains('diana-panel-overlay')) onClose(); }}>
      <div className="diana-panel">
        <div className="diana-header">
          <div className="diana-title">
            <span className="diana-avatar">🤖</span>
            <div>
              <h2>Diana IA</h2>
              <p>Centro de control inteligente</p>
            </div>
          </div>
          <button className="diana-close" onClick={onClose}>✕</button>
        </div>

        <div className="diana-tabs">
          <button className={`diana-tab ${tab === 'pending' ? 'active' : ''}`} onClick={() => setTab('pending')}>
            ❓ Pendientes
            {pending.length > 0 && <span className="diana-badge">{pending.length}</span>}
          </button>
          <button className={`diana-tab ${tab === 'knowledge' ? 'active' : ''}`} onClick={() => setTab('knowledge')}>
            🧠 Conocimiento
            <span className="diana-badge diana-badge-green">{knowledge.length}</span>
          </button>
        </div>

        {tab === 'pending' && (
          <div className="diana-body">
            {pending.length === 0 ? (
              <div className="diana-empty">
                <span style={{ fontSize: '3rem' }}>✅</span>
                <p>No hay preguntas pendientes</p>
                <small>Diana está manejando todo correctamente</small>
              </div>
            ) : (
              <div className="diana-split">
                <div className="diana-list">
                  <div className="diana-list-header">Preguntas sin responder ({pending.length})</div>
                  {pending.map(p => {
                    const name = (p.contactId && (p.contactId.name || p.contactId.profileName)) || p.phoneNumber;
                    return (
                      <div
                        key={p._id}
                        className={`diana-item ${selectedId === p._id ? 'selected' : ''}`}
                        onClick={() => { setSelectedId(p._id); setAnswerText(''); setStatus(''); }}
                      >
                        <div className="diana-item-name">👤 {name}</div>
                        <div className="diana-item-question">
                          {p.imagePath && <span title="Tiene imagen de comprobante" style={{marginRight:'4px'}}>🧾</span>}
                          "{p.question}"
                        </div>
                        <div className="diana-item-time">{timeAgo(p.createdAt)}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="diana-answer-panel">
                  {selectedPending ? (
                    <>
                      <div className="diana-answer-context">
                        <strong>Cliente:</strong> {(selectedPending.contactId && (selectedPending.contactId.name || selectedPending.contactId.profileName)) || selectedPending.phoneNumber}<br />
                        <strong>Pregunta:</strong> {selectedPending.question}<br />
                        <strong>Número:</strong> {selectedPending.phoneNumber}
                        {selectedPending.imagePath && (
                          <div style={{marginTop:'10px'}}>
                            <strong>🧾 Comprobante adjunto:</strong>
                            <div style={{marginTop:'6px'}}>
                              <img
                                src={selectedPending.imagePath.startsWith('/uploads') ? selectedPending.imagePath : `/uploads/media/${selectedPending.imagePath.split('/').pop()}`}
                                alt="Comprobante"
                                style={{maxWidth:'100%', maxHeight:'280px', borderRadius:'8px', border:'2px solid #e2e8f0', cursor:'pointer', display:'block'}}
                                onClick={() => window.open(selectedPending.imagePath.startsWith('/uploads') ? selectedPending.imagePath : `/uploads/media/${selectedPending.imagePath.split('/').pop()}`, '_blank')}
                                onError={e => { e.target.style.display='none'; }}
                              />
                              <small style={{color:'#718096'}}>Click para ampliar</small>
                            </div>
                          </div>
                        )}
                      </div>
                      <textarea
                        className="diana-answer-input"
                        placeholder="Escribe la información o respuesta al admin... Diana la reformulará con su estilo antes de enviar al cliente."
                        value={answerText}
                        onChange={e => setAnswerText(e.target.value)}
                        rows={5}
                      />
                      <div className="diana-save-option">
                        <label>
                          <input
                            type="checkbox"
                            checked={saveToKnowledge}
                            onChange={e => setSaveToKnowledge(e.target.checked)}
                          />
                          &nbsp;Guardar esta respuesta en el conocimiento de Diana
                        </label>
                      </div>
                      <div className="diana-actions">
                        <button
                          className="diana-btn diana-btn-primary"
                          onClick={handleAnswer}
                          disabled={sending || !answerText.trim()}
                        >
                          {sending ? '⏳ Diana está reformulando...' : (saveToKnowledge ? '🤖 Diana Responde y Aprende' : '🤖 Diana Responde')}
                        </button>
                      </div>
                      {status && <div className={`diana-status ${status.startsWith('✅') ? 'success' : status.startsWith('❌') ? 'error' : ''}`}>{status}</div>}
                    </>
                  ) : (
                    <div className="diana-empty">
                      <span style={{ fontSize: '2rem' }}>👈</span>
                      <p>Selecciona una pregunta para responder</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'knowledge' && (
          <div className="diana-body">
            <div className="diana-knowledge-add">
              <h4>➕ Agregar conocimiento manualmente</h4>
              <input
                className="diana-input"
                placeholder="Pregunta o tema (ej: ¿Tienen planes para empresas?)"
                value={manualQ}
                onChange={e => setManualQ(e.target.value)}
              />
              <textarea
                className="diana-answer-input"
                placeholder="Respuesta que Diana dará..."
                value={manualA}
                onChange={e => setManualA(e.target.value)}
                rows={3}
              />
              <button
                className="diana-btn diana-btn-primary"
                onClick={handleAddKnowledge}
                disabled={addingKnowledge || !manualQ.trim() || !manualA.trim()}
              >
                {addingKnowledge ? '⏳ Guardando...' : '💾 Guardar'}
              </button>
            </div>

            <div className="diana-knowledge-list">
              <div className="diana-list-header">Base de conocimiento ({knowledge.length} entradas)</div>
              {knowledge.length === 0 ? (
                <div className="diana-empty">
                  <span style={{ fontSize: '2rem' }}>🧠</span>
                  <p>Aún no hay conocimiento guardado</p>
                  <small>Responde preguntas pendientes con "Guardar en conocimiento" activado</small>
                </div>
              ) : (
                knowledge.map(e => (
                  <div key={e._id} className="diana-knowledge-item">
                    <div className="diana-knowledge-q">❓ {e.question}</div>
                    <div className="diana-knowledge-a">💬 {e.answer}</div>
                    <div className="diana-knowledge-meta">
                      Usado {e.useCount || 0} veces · {timeAgo(e.createdAt)}
                      <button className="diana-delete-btn" onClick={() => handleDeleteKnowledge(e._id)}>🗑️</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
